import { RotateCw } from "lucide-react";
import React, { useState } from "react";
import { motion } from "framer-motion";

interface ExportItem {
  date: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

interface TableSectionProps {
  data: ExportItem[];
  onRefresh?: () => void;
}

const TableSection: React.FC<TableSectionProps> = ({ data, onRefresh }) => {
  const [loading, setLoading] = useState(false);

  const handleRefresh = () => {
    if (!onRefresh) return;
    setLoading(true);
    onRefresh();
    setTimeout(() => setLoading(false), 1000);
  };

  return (
    <motion.div
      role="region"
      aria-labelledby="table-section-title"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2
        id="table-section-title"
        className="text-4xl font-bold text-red-800 text-center"
      >
        Burn & Mint Table
      </h2>

      <div className="relative flex flex-col justify-center p-10 w-full max-w-7xl mx-auto bg-white/90 rounded-2xl shadow-xl gap-6 mt-8">
        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={handleRefresh}
            className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow flex items-center absolute top-4 right-4"
            aria-label="Refresh table"
          >
            <RotateCw
              className={`w-5 h-5 transition-transform ${
                loading ? "animate-spin" : ""
              }`}
            />
          </button>
        )}

        {/* Table or fallback */}
        {data.length === 0 ? (
          <p className="text-gray-500 italic text-center py-8">
            ⚠️ No data available for this range.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-blue-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 border-b text-left">📅 Date</th>
                  <th className="px-4 py-2 border-b text-left text-black">
                    🔥 NAU Burned
                  </th>
                  <th className="px-4 py-2 border-b text-left text-blue-700">
                    🔵 NAUY Minted
                  </th>
                  <th className="px-4 py-2 border-b text-left text-red-700">
                    🔴 NAUN Minted
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => (
                  <tr
                    key={item.date + idx}
                    className="text-center hover:bg-gray-50"
                  >
                    <td className="border-t px-4 py-2">{item.date}</td>
                    <td className="border-t px-4 py-2 text-black font-semibold">
                      {item.NAUBurn.toLocaleString()}
                    </td>
                    <td className="border-t px-4 py-2 text-blue-700">
                      {item.NAUYMint.toLocaleString()}
                    </td>
                    <td className="border-t px-4 py-2 text-red-700">
                      {item.NAUNMint.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TableSection;
